import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertTriangle,
  Bell,
  BellRing,
  ChevronLeft,
  Clock,
  Edit,
  Loader2,
  Plus,
  Play,
  Trash2,
  TrendingDown,
  Target,
  Percent,
  ArrowDownRight,
  Check,
  X,
  Zap,
  RefreshCw,
  Mail,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { AlertRuleFormDialog } from '../components/alerts/AlertRuleFormDialog';
import LeiDivergenceDigestCard from '../components/alerts/LeiDivergenceDigestCard';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlertRulesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [presets, setPresets] = useState([]);
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    alert_type: 'threshold_breach',
    metric_type: 'both',
    threshold_value: 50,
    enabled: true,
    schedule_frequency: 'manual',
    email_notifications: false,
    notification_emails: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = secureStorage.getItem('session_token');
      
      const [rulesRes, presetsRes, alertsRes] = await Promise.all([
        authedFetch(`${API_URL}/api/alert-rules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        authedFetch(`${API_URL}/api/alert-rules/presets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        authedFetch(`${API_URL}/api/alert-rules/triggered-alerts?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (rulesRes.ok) {
        const data = await rulesRes.json();
        setRules(data.rules || []);
      }
      
      if (presetsRes.ok) {
        const data = await presetsRes.json();
        setPresets(data.presets || []);
      }
      
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setTriggeredAlerts(data.alerts || []);
      }
    } catch (err) {
      logger.error('Error fetching data:', err);
      toast.error('Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      
      // Parse notification emails from comma-separated string to array
      const payload = {
        ...formData,
        notification_emails: formData.notification_emails 
          ? formData.notification_emails.split(',').map(e => e.trim()).filter(e => e)
          : []
      };
      
      const response = await authedFetch(`${API_URL}/api/alert-rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Failed to create rule');
      
      toast.success('Alert rule created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateRule = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      
      // Parse notification emails from comma-separated string to array
      const payload = {
        ...formData,
        notification_emails: formData.notification_emails 
          ? formData.notification_emails.split(',').map(e => e.trim()).filter(e => e)
          : []
      };
      
      const response = await authedFetch(`${API_URL}/api/alert-rules/${editingRule.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Failed to update rule');
      
      toast.success('Alert rule updated successfully');
      setEditingRule(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this alert rule?')) return;
    
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/alert-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete rule');
      
      toast.success('Alert rule deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/alert-rules/${ruleId}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to toggle rule');
      
      const data = await response.json();
      toast.success(`Alert rule ${data.enabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCheckNow = async () => {
    try {
      setCheckingAlerts(true);
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/alert-rules/check-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to check alerts');
      
      const data = await response.json();
      
      if (data.alerts_triggered > 0) {
        toast.success(`${data.alerts_triggered} alert(s) triggered!`, {
          description: `Checked ${data.rules_checked} rules across ${data.cohorts_checked} cohorts`
        });
      } else {
        toast.info('No alerts triggered', {
          description: `Checked ${data.rules_checked} rules across ${data.cohorts_checked} cohorts`
        });
      }
      
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCheckingAlerts(false);
    }
  };

  const handleApplyPreset = (preset) => {
    setFormData({
      name: preset.name,
      description: preset.description,
      alert_type: preset.alert_type,
      metric_type: preset.metric_type,
      threshold_value: preset.threshold_value,
      enabled: true,
      schedule_frequency: 'manual',
      email_notifications: false,
      notification_emails: ''
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      alert_type: 'threshold_breach',
      metric_type: 'both',
      threshold_value: 50,
      enabled: true,
      schedule_frequency: 'manual',
      email_notifications: false,
      notification_emails: ''
    });
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      alert_type: rule.alert_type,
      metric_type: rule.metric_type,
      threshold_value: rule.threshold_value,
      enabled: rule.enabled,
      schedule_frequency: rule.schedule_frequency || 'manual',
      email_notifications: rule.email_notifications || false,
      notification_emails: (rule.notification_emails || []).join(', ')
    });
  };

  const getScheduleLabel = (freq) => {
    switch (freq) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return 'Manual';
    }
  };

  const getScheduleBadgeClass = (freq) => {
    switch (freq) {
      case 'daily': return 'bg-emerald-100 text-emerald-700';
      case 'weekly': return 'bg-blue-100 text-blue-700';
      case 'monthly': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'threshold_breach': return <Target className="h-4 w-4" />;
      case 'decline_percent': return <Percent className="h-4 w-4" />;
      case 'decline_points': return <ArrowDownRight className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'threshold_breach': return 'Below Threshold';
      case 'decline_percent': return '% Decline';
      case 'decline_points': return 'Point Drop';
      default: return type;
    }
  };

  const getMetricBadge = (type) => {
    switch (type) {
      case 'lei': return <Badge className="bg-indigo-100 text-indigo-700">LEI Only</Badge>;
      case 'kpi': return <Badge className="bg-purple-100 text-purple-700">KPI Only</Badge>;
      case 'both': return <Badge className="bg-blue-100 text-blue-700">LEI & KPI</Badge>;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Reengage');
  const alertHeroActions = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleCheckNow}
        disabled={checkingAlerts || rules.filter(r => r.enabled).length === 0}
        className="gap-1.5 bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        data-testid="check-now-btn"
      >
        {checkingAlerts ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
        Check Now
      </Button>
      <Button size="sm" onClick={() => setShowCreateModal(true)} className="gap-1.5 bg-white text-cyan-700 hover:bg-slate-50" data-testid="create-rule-btn">
        <Plus size={14} /> Create Rule
      </Button>
    </div>
  );
  const alertHeroStats = [
    { label: 'Total Rules', value: rules.length, testId: 'alerts-stat-total' },
    { label: 'Active', value: rules.filter(r => r.enabled).length, testId: 'alerts-stat-active' },
    { label: 'Recent Alerts', value: triggeredAlerts.length, testId: 'alerts-stat-recent' },
    { label: 'Total Triggers', value: rules.reduce((sum, r) => sum + (r.trigger_count || 0), 0), testId: 'alerts-stat-triggers' },
  ];

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Alert Rules"
        description="Configure rule-based alerts so the team gets pinged the moment a cohort score crosses a threshold — instead of finding out three weeks later in the next monthly review. Use presets to bootstrap, then tune rules as your portfolio matures."
        theme={__pageTheme}
        icon={BellRing}
        actions={alertHeroActions}
        stats={alertHeroStats}
        testId="alert-rules-hero"
      />
    )}>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/30" data-testid="alert-rules-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/program-analytics')}
            className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors"
            data-testid="back-to-analytics"
          >
            <ChevronLeft size={20} />
            Back to Program Analytics
          </button>
        </div>

        <LeiDivergenceDigestCard />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Alert Rules List */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Bell size={20} />
              Your Alert Rules
            </h2>
            
            {rules.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BellRing className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">No alert rules yet</h3>
                  <p className="text-gray-500 mb-4">
                    Create your first alert rule or use a preset to get started.
                  </p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="mr-2" size={16} />
                    Create Rule
                  </Button>
                </CardContent>
              </Card>
            ) : (
              rules.map((rule) => (
                <Card 
                  key={rule.id} 
                  className={`transition-all ${!rule.enabled ? 'opacity-60' : ''}`}
                  data-testid={`alert-rule-card-${rule.id}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2 rounded-lg ${rule.enabled ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                            {getAlertTypeIcon(rule.alert_type)}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{rule.name}</h3>
                            <p className="text-sm text-gray-500">{rule.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <Badge variant="outline" className="flex items-center gap-1">
                            {getAlertTypeIcon(rule.alert_type)}
                            {getAlertTypeLabel(rule.alert_type)}
                          </Badge>
                          {getMetricBadge(rule.metric_type)}
                          <Badge variant="secondary">
                            Threshold: {rule.threshold_value}
                            {rule.alert_type === 'decline_percent' ? '%' : ' pts'}
                          </Badge>
                          {rule.schedule_frequency && rule.schedule_frequency !== 'manual' && (
                            <Badge className={getScheduleBadgeClass(rule.schedule_frequency)}>
                              {getScheduleLabel(rule.schedule_frequency)}
                            </Badge>
                          )}
                          {rule.email_notifications && (
                            <Badge className="bg-indigo-100 text-indigo-700">
                              Email
                            </Badge>
                          )}
                          {rule.trigger_count > 0 && (
                            <Badge className="bg-amber-100 text-amber-700">
                              {rule.trigger_count} triggers
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-400">
                          {rule.last_triggered_at && (
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              Last triggered: {new Date(rule.last_triggered_at).toLocaleDateString()}
                            </span>
                          )}
                          {rule.next_scheduled_check && rule.schedule_frequency !== 'manual' && (
                            <span className="flex items-center gap-1 text-indigo-500">
                              <RefreshCw size={12} />
                              Next check: {new Date(rule.next_scheduled_check).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={() => handleToggleRule(rule.id)}
                          data-testid={`toggle-rule-${rule.id}`}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(rule)}
                          data-testid={`edit-rule-${rule.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="text-rose-500 hover:text-rose-700"
                          data-testid={`delete-rule-${rule.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Sidebar: Presets & Recent Alerts */}
          <div className="space-y-6">
            {/* Presets */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap size={18} className="text-indigo-600" />
                  Quick Presets
                </CardTitle>
                <CardDescription>One-click alert rule templates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {presets.map((preset, idx) => (
                  <button
                    key={preset.name || `preset-${idx}`}
                    onClick={() => handleApplyPreset(preset)}
                    className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all group"
                    data-testid={`preset-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 group-hover:text-indigo-700">
                        {preset.name}
                      </span>
                      <Plus size={16} className="text-gray-400 group-hover:text-indigo-600" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{preset.description}</p>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Recent Triggered Alerts */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle size={18} className="text-amber-600" />
                  Recent Alerts
                </CardTitle>
                <CardDescription>Latest triggered alerts</CardDescription>
              </CardHeader>
              <CardContent>
                {triggeredAlerts.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No alerts triggered yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {triggeredAlerts.slice(0, 5).map((alert) => (
                      <div
                        key={alert.id}
                        className="p-3 bg-gray-50 rounded-lg border-l-4 border-amber-400"
                      >
                        <p className="font-medium text-gray-900 text-sm">
                          {alert.cohort_name}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">{alert.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(alert.triggered_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <AlertRuleFormDialog
          showCreateModal={showCreateModal}
          editingRule={editingRule}
          setShowCreateModal={setShowCreateModal}
          setEditingRule={setEditingRule}
          resetForm={resetForm}
          formData={formData}
          setFormData={setFormData}
          handleCreateRule={handleCreateRule}
          handleUpdateRule={handleUpdateRule}
        />
      </div>
    </div>
    </PageShell>
  );
}
